import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Settings, Plus, Eye, Download, Clock, CheckCircle, Edit, Trash2, Search, Filter } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';
import AgreementFormatSidebar from '@/components/agreements/AgreementFormatSidebar';

export default function AgreementsList() {
  const navigate = useNavigate();
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [templateConfig, setTemplateConfig] = useState(null);

  useEffect(() => {
    fetchAgreements();
    fetchTemplateConfig();
  }, []);

  const fetchAgreements = async () => {
    try {
      setLoading(true);
      const response = await api.get('/agreements');
      setAgreements(response.data.agreements || []);
    } catch (error) {
      console.error('Error fetching agreements:', error);
      toast.error('Failed to load agreements');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplateConfig = async () => {
    try {
      const response = await api.get('/agreements/template-config');
      setTemplateConfig(response.data);
    } catch (error) {
      console.log('No template config found');
    }
  };

  const handleSaveTemplateConfig = async (config) => {
    try {
      await api.post('/agreements/template-config', config);
      setTemplateConfig(config);
      toast.success('Template configuration saved');
    } catch (error) {
      console.error('Error saving template config:', error);
      throw error;
    }
  };

  const handleDelete = async (agreementId) => {
    if (!window.confirm('Are you sure you want to delete this agreement?')) {
      return;
    }

    try {
      await api.delete(`/agreements/${agreementId}`);
      toast.success('Agreement deleted');
      fetchAgreements();
    } catch (error) {
      console.error('Error deleting agreement:', error);
      toast.error('Failed to delete agreement');
    }
  };

  const filteredAgreements = agreements.filter(agreement => {
    const matchesSearch = agreement.proposal_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'draft' && (agreement.status === 'draft' || agreement.completion_status === 'pending_completion')) ||
      (statusFilter === 'final' && agreement.status === 'final' && agreement.completion_status !== 'pending_completion');
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-amber-100 text-amber-700 border-amber-200',
      final: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    };

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border ${styles[status] || 'bg-slate-100 text-slate-700 border-slate-200'}`}>
        {status === 'final' ? <CheckCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading agreements...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      {/* Agreement Format Sidebar */}
      <AgreementFormatSidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        config={templateConfig}
        onSave={handleSaveTemplateConfig}
      />

      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-indigo-100 rounded-lg">
                <FileText className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">SaaS Agreements</h1>
                <p className="text-sm text-slate-600">Manage service agreements and templates</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/agreement-template')}
              className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-800 text-white rounded-lg font-medium transition-colors"
            >
              <Settings className="w-4 h-4" />
              Configure Template
            </button>
          </div>

          {/* Search and Filter */}
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search agreements by proposal name..."
                className="input-field pl-10 w-full"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input-field"
            >
              <option value="all">All Status</option>
              <option value="draft">Draft / Pending Completion</option>
              <option value="final">Final</option>
            </select>
          </div>

          <div className="mt-4 flex items-center justify-between text-sm">
            <p className="text-slate-600">
              {filteredAgreements.length} agreement{filteredAgreements.length !== 1 ? 's' : ''} found
            </p>
          </div>
        </div>

        {/* Agreements List */}
        {filteredAgreements.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No Agreements Found</h3>
            <p className="text-slate-600 mb-4">
              {searchTerm || statusFilter !== 'all' 
                ? 'No agreements match your filters'
                : 'Get started by approving a proposal and generating an agreement'
              }
            </p>
            <button
              onClick={() => navigate('/approvals')}
              className="btn-primary"
            >
              Go to Approvals
            </button>
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredAgreements.map((agreement) => (
              <div
                key={agreement.id}
                className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-bold text-slate-900">{agreement.proposal_name}</h3>
                      {getStatusBadge(agreement.status, agreement.completion_status, agreement.auto_created)}
                      {agreement.auto_created && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-medium">
                          Auto-Created
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-slate-600">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        Created: {new Date(agreement.created_at).toLocaleDateString()}
                      </span>
                      <span>
                        Services: {agreement.services?.length || 0}
                      </span>
                      {agreement.created_by && (
                        <span>By: {agreement.created_by}</span>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-2">
                    {(agreement.status === 'draft' || agreement.completion_status === 'pending_completion') ? (
                      <button
                        onClick={() => navigate(`/agreement/${agreement.collection_id}`)}
                        className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white hover:bg-emerald-600 rounded-lg font-medium transition-colors text-sm"
                      >
                        <Edit className="w-4 h-4" />
                        Complete Agreement
                      </button>
                    ) : (
                      <button
                        onClick={() => navigate(`/agreement/${agreement.collection_id}`)}
                        className="flex items-center gap-2 px-3 py-2 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 rounded-lg font-medium transition-colors text-sm"
                      >
                        <Eye className="w-4 h-4" />
                        View
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(agreement.id)}
                      className="flex items-center gap-2 px-3 py-2 bg-red-50 text-red-700 hover:bg-red-100 rounded-lg font-medium transition-colors text-sm"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </div>
                </div>

                {/* Services Summary */}
                {agreement.services && agreement.services.length > 0 && (
                  <div className="border-t border-slate-100 pt-4">
                    <p className="text-xs font-semibold text-slate-700 mb-2">Services Included:</p>
                    <div className="flex flex-wrap gap-2">
                      {agreement.services.slice(0, 5).map((service, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center px-2 py-1 bg-slate-100 text-slate-700 rounded text-xs"
                        >
                          {service.service_name}
                          {service.reader_cost_recovery?.applied && (
                            <CheckCircle className="w-3 h-3 ml-1 text-green-600" />
                          )}
                        </span>
                      ))}
                      {agreement.services.length > 5 && (
                        <span className="text-xs text-slate-500">
                          +{agreement.services.length - 5} more
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Notes Preview */}
                {agreement.notes && (
                  <div className="border-t border-slate-100 pt-4 mt-4">
                    <p className="text-xs font-semibold text-slate-700 mb-1">Notes:</p>
                    <p className="text-sm text-slate-600 line-clamp-2">{agreement.notes}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
